import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X, AlertTriangle, Search, Check } from "lucide-react";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

// Section 8 — outcome catalogue. Kept in a single source of truth so the
// resulting-status pill next to each radio never drifts from the backend
// mapping in routes/trial_outcomes.py.
export const OUTCOME_OPTIONS = [
  { value: "converted",          label: "Converted",           subtitle: "Customer completed a rental booking", status: "converted",          requiresBooking: true },
  { value: "follow_up",          label: "Follow up",           subtitle: "Attended, still deciding — requires action", status: "follow_up" },
  { value: "did_not_convert",    label: "Did not convert",     subtitle: "Attended but did not book", status: "closed" },
  { value: "no_show",            label: "No-show",             subtitle: "Customer did not attend", status: "no_show" },
  { value: "customer_cancelled", label: "Customer cancelled",  subtitle: "Customer notified they can't make it", status: "customer_cancelled" },
  { value: "admin_cancelled",    label: "Admin cancelled",     subtitle: "We cancelled the appointment", status: "admin_cancelled" },
  { value: "rescheduled",        label: "Rescheduled",         subtitle: "Moved to a different date — reschedule history preserved", status: "rescheduled" },
  { value: "could_not_contact",  label: "Could not contact",   subtitle: "Attempts to reach customer failed", status: "closed" },
  { value: "duplicate",          label: "Duplicate",           subtitle: "Same lead / trial already exists", status: "closed" },
  { value: "other",              label: "Other",               subtitle: "Notes required", status: "closed",              requiresNotes: true },
];

// ─── Booking picker ───────────────────────────────────────────────────────
// Scoped to bookings that match the appointment's customer_email (or name
// fallback) so the admin only sees plausible matches. Free-text search lets
// them widen the query when the email doesn't match exactly.
function BookingPicker({ appointment, value, onChange }) {
  const [q, setQ] = useState(appointment?.customer_email || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/bookings`, { headers: auth() });
        if (cancelled) return;
        setRows(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        console.warn("[CloseTrialModal] bookings fetch failed", e);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows.slice(0, 12);
    return rows.filter(b => {
      const hay = [b.id, b.customer_name, b.customer_email, b.customer_phone, b.outfit_name].join(" ").toLowerCase();
      return hay.includes(qq);
    }).slice(0, 20);
  }, [rows, q]);

  const selected = rows.find(b => b.id === value);

  return (
    <div className="space-y-2" data-testid="booking-picker">
      <label className="text-[11px] uppercase tracking-wider text-[#676664]/70 font-medium">Link to booking (required)</label>
      {selected && !expanded ? (
        <div className="border border-[#9A7B4F]/40 bg-[#FAF3E0]/40 p-3 flex items-start justify-between gap-3" data-testid="booking-picker-selected">
          <div className="text-[12px]">
            <div className="font-medium text-[#282828]">{selected.customer_name} — {selected.outfit_name || "—"}</div>
            <div className="text-[11px] text-[#676664] mt-0.5">ID <span className="font-mono">{selected.id}</span> · Event {selected.event_date || "—"}</div>
          </div>
          <button type="button" onClick={() => setExpanded(true)} className="text-[11px] text-[#9A7B4F] hover:underline" data-testid="booking-picker-change">Change</button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[#676664]/50" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, email, phone, or booking ID"
              className="w-full border border-[#282828]/15 pl-8 pr-3 py-2 text-[12px] focus:outline-none focus:border-[#9A7B4F]"
              data-testid="booking-picker-search"
            />
          </div>
          <div className="max-h-48 overflow-y-auto border border-[#282828]/8 divide-y divide-[#282828]/6">
            {loading && (
              <div className="p-3 text-[11px] text-[#676664]/60 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading bookings…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="p-3 text-[11px] text-[#676664]/60">No matching bookings — try a different search.</div>
            )}
            {filtered.map(b => (
              <button
                key={b.id}
                type="button"
                onClick={() => { onChange(b.id); setExpanded(false); }}
                className={`w-full text-left px-3 py-2 hover:bg-[#FAF8F0] ${value === b.id ? "bg-[#FAF3E0]/40" : ""}`}
                data-testid={`booking-picker-option-${b.id}`}
              >
                <div className="flex items-center justify-between gap-2 text-[12px]">
                  <span className="font-medium text-[#282828] truncate">{b.customer_name || "—"}</span>
                  {value === b.id && <Check className="w-3.5 h-3.5 text-[#9A7B4F]" />}
                </div>
                <div className="text-[10px] text-[#676664]/70 mt-0.5 font-mono truncate">{b.id} · {b.outfit_name || "—"} · {b.event_date || "—"}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────
// Section 8 Rev 2 — reusable for BOTH trial appointments AND trial leads.
// Pass either `appointment={...}` (kind defaults to "appointment") or
// `entity={{id, ...}} entityKind="trial_lead"` (or "appointment").
// The endpoint URL, wording, and date field auto-adapt.
export function CloseTrialModal({ appointment, entity, entityKind, onClose, onClosed }) {
  // Normalise inputs so the rest of the component is entity-shape-agnostic.
  const src = entity || appointment || {};
  const kind = entityKind || (entity ? "appointment" : "appointment");
  const dateStr = src.date || src.preferred_date || "";
  const timeStr = src.time || src.preferred_time || "";
  const nounSingular = kind === "trial_lead" ? "trial lead" : "appointment";
  const endpointBase = kind === "trial_lead"
    ? `${API}/admin/trial-leads/${src.id}`
    : `${API}/admin/appointments/${src.id}`;

  const [outcome, setOutcome] = useState(src.outcome || "");
  const [notes, setNotes] = useState(src.outcome_notes || "");
  const [bookingId, setBookingId] = useState(src.linked_booking_id || "");
  const [submitting, setSubmitting] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [showFutureWarn, setShowFutureWarn] = useState(false);

  const isAlreadyClosed = Boolean(src.outcome);

  // Section 8 — same-day / next-day logic reuses Australia/Melbourne.
  const isFuture = useMemo(() => {
    if (!dateStr) return false;
    try {
      const melTodayStr = new Date().toLocaleString("en-CA", { timeZone: "Australia/Melbourne", year: "numeric", month: "2-digit", day: "2-digit" });
      return dateStr > melTodayStr;
    } catch { return false; }
  }, [dateStr]);

  const opt = OUTCOME_OPTIONS.find(o => o.value === outcome);
  const requiresBooking = opt?.requiresBooking;
  const requiresNotes = opt?.requiresNotes;

  const canSubmit = (
    !!outcome
    && (!requiresBooking || bookingId.trim().length > 0)
    && (!requiresNotes || notes.trim().length > 0)
    && notes.length <= 500
  );

  const doSubmit = async (forceFuture = false) => {
    if (!canSubmit) return;
    if (isFuture && !forceFuture && !isAlreadyClosed) {
      setShowFutureWarn(true);
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        outcome,
        outcome_notes: notes.trim(),
        linked_booking_id: requiresBooking ? bookingId.trim() : "",
        force_future: forceFuture,
      };
      const r = await axios.post(`${endpointBase}/close`, body, { headers: auth() });
      const corrected = r.data?.corrected;
      const synced = r.data?.linked_lead_updated || r.data?.linked_appointment_updated;
      const successMsg = corrected
        ? (synced ? "Outcome updated (linked entity synced)" : "Outcome updated")
        : (synced ? `${kind === "trial_lead" ? "Lead" : "Trial"} closed (linked entity synced)` : `${kind === "trial_lead" ? "Lead" : "Trial"} closed`);
      toast.success(successMsg);
      onClosed?.(r.data.lead || r.data.appointment);
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `Could not close ${nounSingular}`);
    } finally {
      setSubmitting(false);
      setShowFutureWarn(false);
    }
  };

  const doReopen = async () => {
    const reason = window.prompt("Reason for reopening (optional):", "");
    if (reason === null) return; // cancelled
    setReopening(true);
    try {
      const r = await axios.post(`${endpointBase}/reopen`, { reason }, { headers: auth() });
      const synced = r.data?.linked_lead_updated || r.data?.linked_appointment_updated;
      toast.success(synced ? `${kind === "trial_lead" ? "Lead" : "Trial"} reopened (linked entity synced)` : `${kind === "trial_lead" ? "Lead" : "Trial"} reopened — outcome cleared`);
      onClosed?.(r.data.lead || r.data.appointment);
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reopen failed");
    } finally { setReopening(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="close-trial-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-start justify-between p-5 border-b border-[#282828]/10">
          <div>
            <h3 className="text-[15px] text-[#282828]">{isAlreadyClosed ? "Update trial outcome" : `Close ${nounSingular} / Mark outcome`}</h3>
            <p className="text-[11px] text-[#676664]/70 mt-1">
              {src?.customer_name || src?.name || "—"} · {kind === "trial_lead" ? "Trial lead" : "Trial"} on <span className="font-medium">{dateStr || "—"}</span>{timeStr ? ` at ${timeStr}` : ""}
              {isAlreadyClosed && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#9A7B4F]">Currently: {src.outcome}</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-[#676664]/40 hover:text-[#282828] p-1" data-testid="close-trial-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {isFuture && (
            <div className="bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-800 flex items-start gap-2" data-testid="future-trial-notice">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Heads up:</strong> This {nounSingular} is scheduled for a future date ({dateStr}). Closing it now records the outcome immediately — you can reopen it later if needed.
              </div>
            </div>
          )}

          {/* Outcome radios */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#676664]/70 font-medium block mb-2">Outcome</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {OUTCOME_OPTIONS.map(o => (
                <label
                  key={o.value}
                  className={`border p-3 cursor-pointer transition-colors ${outcome === o.value ? "border-[#9A7B4F] bg-[#FAF3E0]/40" : "border-[#282828]/12 hover:border-[#9A7B4F]/50"}`}
                  data-testid={`outcome-radio-${o.value}`}
                >
                  <div className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="outcome"
                      value={o.value}
                      checked={outcome === o.value}
                      onChange={() => setOutcome(o.value)}
                      className="mt-1 accent-[#9A7B4F]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[12px] font-medium text-[#282828]">{o.label}</span>
                        <span className="text-[9px] uppercase tracking-wider text-[#9A7B4F] bg-[#9A7B4F]/8 px-1.5 py-0.5">{o.status.replace("_", " ")}</span>
                      </div>
                      <div className="text-[10px] text-[#676664]/70 mt-0.5">{o.subtitle}</div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Linked booking selector — only for converted */}
          {requiresBooking && (
            <BookingPicker appointment={src} value={bookingId} onChange={setBookingId} />
          )}

          {/* Notes */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#676664]/70 font-medium block mb-2">
              Notes {requiresNotes && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 500))}
              rows={3}
              placeholder={requiresNotes ? "Required — briefly describe the outcome…" : "Optional context for future reference…"}
              className="w-full border border-[#282828]/15 px-3 py-2 text-[12px] focus:outline-none focus:border-[#9A7B4F] resize-none"
              data-testid="outcome-notes-input"
            />
            <div className="flex justify-end text-[10px] text-[#676664]/50 mt-1">{notes.length}/500</div>
          </div>
        </div>

        <div className="border-t border-[#282828]/10 p-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            {isAlreadyClosed && (
              <Button
                variant="outline"
                onClick={doReopen}
                disabled={reopening || submitting}
                className="text-[11px] h-8 px-3 text-[#9A7B4F] border-[#9A7B4F]/30 hover:bg-[#9A7B4F]/5"
                data-testid="reopen-trial-btn"
              >
                {reopening ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                Reopen {nounSingular}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} className="text-[11px] h-8 px-4" data-testid="close-trial-cancel-btn">Cancel</Button>
            <Button
              onClick={() => doSubmit(false)}
              disabled={!canSubmit || submitting}
              className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-4"
              data-testid="close-trial-submit-btn"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              {isAlreadyClosed ? "Update outcome" : `Close ${nounSingular}`}
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation for closing a future trial */}
      {showFutureWarn && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowFutureWarn(false)} data-testid="future-warn-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-sm w-full shadow-2xl">
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <h4 className="text-[14px] text-[#282828]">Close a future trial?</h4>
              </div>
              <p className="text-[12px] text-[#676664] leading-relaxed">
                This {nounSingular} is scheduled for {dateStr}. Closing it now marks the outcome immediately. You can reopen it later if that turns out to be premature.
              </p>
            </div>
            <div className="border-t border-[#282828]/10 p-3 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowFutureWarn(false)} className="text-[11px] h-8 px-3" data-testid="future-warn-cancel">Not yet</Button>
              <Button onClick={() => doSubmit(true)} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] h-8 px-3" data-testid="future-warn-confirm">Yes, close it</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Compact read-only display of a closed appointment's outcome.
export function OutcomeSummary({ appointment }) {
  if (!appointment?.outcome) return null;
  const opt = OUTCOME_OPTIONS.find(o => o.value === appointment.outcome);
  return (
    <div className="bg-[#FAF3E0]/40 border border-[#9A7B4F]/25 p-3" data-testid="outcome-summary">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#676664]/70 font-medium">Closed outcome</div>
          <div className="text-[13px] font-medium text-[#282828] mt-0.5">{opt?.label || appointment.outcome}</div>
          {appointment.outcome_notes && (
            <div className="text-[11px] text-[#5A5A5A] mt-1 whitespace-pre-wrap">{appointment.outcome_notes}</div>
          )}
          {appointment.linked_booking_id && (
            <div className="text-[10px] font-mono text-[#676664]/70 mt-1">Linked booking: {appointment.linked_booking_id}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-[9px] uppercase tracking-wider text-[#9A7B4F] bg-[#9A7B4F]/10 px-1.5 py-0.5 inline-block">{(opt?.status || "closed").replace("_", " ")}</div>
          {appointment.closed_by && (
            <div className="text-[9px] text-[#676664]/50 mt-1">by {appointment.closed_by}</div>
          )}
        </div>
      </div>
    </div>
  );
}
